import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { ABOUT_BIO, CREDENTIALS, EDUCATION, SOFTWARE, HERO_PORTRAIT } from "@/data/content";

export const Profile = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="hakkimda" className="relative bg-ink py-28 md:py-40 px-6 md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-8">Hakkımda</p>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-sand max-w-3xl mb-14 md:mb-20">
            Yatırım ve inşaatın içinden bir mühendis.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div ref={ref} className="relative overflow-hidden rounded-sm h-[420px] md:h-[560px]">
              <motion.img
                style={{ y: imgY, scale: 1.18 }}
                src={HERO_PORTRAIT}
                alt="Cagatay Turna"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="profile-image"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-hairline" />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-ink via-ink/70 to-transparent">
                <p className="font-semibold text-2xl text-sand">Cagatay Turna</p>
                <p className="text-xs tracking-[0.2em] uppercase text-gold mt-1">İnşaat Yüksek Mühendisi</p>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {CREDENTIALS.map((s, i) => (
                <Reveal key={s.label} delay={i}>
                  <div className="rounded-sm border border-hairline bg-surface px-3 py-6 text-center">
                    <p className="font-semibold text-3xl md:text-4xl text-gold">{s.value}</p>
                    <p className="mt-2 text-[11px] tracking-[0.1em] uppercase text-mist">{s.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            {ABOUT_BIO.map((para, i) => (
              <Reveal key={i} delay={i}>
                <p className="text-base md:text-lg font-light leading-relaxed text-mist max-w-xl mb-6">
                  {para}
                </p>
              </Reveal>
            ))}

            <Reveal delay={2}>
              <p className="mt-8 text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-5">Eğitim</p>
            </Reveal>
            <div className="border-t border-hairline">
              {EDUCATION.map((e, i) => (
                <Reveal
                  key={i}
                  delay={i}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-4 py-5 border-b border-hairline"
                >
                  <span className="sm:col-span-3 text-sm text-gold/80">{e.period}</span>
                  <div className="sm:col-span-9">
                    <p className="text-sand font-medium">{e.title}</p>
                    <p className="text-sm text-mist">{e.place}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={1}>
              <p className="mt-10 text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-5">Teknik Yazılımlar</p>
            </Reveal>
            <div className="flex flex-wrap gap-2.5">
              {SOFTWARE.map((s) => (
                <span
                  key={s}
                  data-testid={`software-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="rounded-full border border-hairline bg-surface px-4 py-2 text-xs tracking-wide text-sand/80"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
